// Random matches: the pool, and the pipe it runs down.
//
// The pool is one request that the server holds open. Opening it is joining it, so a
// player who closes the tab has left by definition and the room cannot be left dirty.
// Four things come back down it: who you are (hello), who has turned up (matched), what
// they threw (shot) and that they are gone (gone).
//
// Nothing here understands the game. A shot is the match code the game already knows how
// to build and how to apply — city, round, turn, score, craters — so the only thing this
// module moves is a string, and the rules of the game stay on the two machines at the ends
// of it.
//
// Everything fails soft and in one direction: no server, no pool, nobody there, a stream
// that dies — all of them end in "no rival", which the caller answers with a CPU match. A
// button that can fail to do anything at all is worse than no button.

import { POOL_URL, POOL_SHOT_URL, POOL_STATS_URL, POOL_WAIT_MS, ROOM_SHOT_URL, ROOM_URL } from './config.js';

/** The ticket the server gave us, which is what a shot is posted with. */
let ticket = null;
/** The request that is holding the stream open, so it can be let go of. */
let stream = null;
let seat = 0;
let rival = '';
/**
 * The room we are in, if we are in one: `{ code, id, seat, token, abort }`.
 *
 * Null when the match is not a room — a pool match has its own ticket and a duel by
 * correspondence has no pipe at all — which is what `sendShot` reads to know where a turn
 * has to go.
 */
let room = null;

export function poolSeat() {
  return seat;
}

export function poolRival() {
  return rival;
}

/** Whether there is a pool stream open right now: the match is travelling down it. */
export function inPool() {
  return !!stream;
}

/**
 * A frame handed to the game without the reader dying with it.
 *
 * Reading the stream and running the game's callback shared one `try`, so an exception on the
 * way in — applying the rival's code, drawing the result — left the loop for dead: no error on
 * screen and no more frames read, which is the rival going mute for the rest of the match. The
 * failure is said here and the stream carries on.
 */
function deliver(fn, data) {
  try {
    fn(data);
  } catch (err) {
    console.error('net: frame handler failed', err);
  }
}

/**
 * One SSE frame into an event. Comments (`: ping`) and blank fields are not events, and
 * the ping is the whole reason the server sends them.
 */
function framed(chunk) {
  let event = 'message';
  const data = [];

  for (const line of chunk.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).trim());
  }

  if (!data.length) return null;

  try {
    return { event, data: JSON.parse(data.join('\n')) };
  } catch {
    return null;
  }
}

/** How many are waiting for a match, or null if that could not be found out. */
export async function poolStatus() {
  try {
    const res = await fetch(POOL_STATS_URL, { headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      waiting: Number(data && data.waiting) || 0,
      playing: Number(data && data.playing) || 0,
    };
  } catch {
    // No server at all is the normal case for a game run from a folder, and it is not an
    // error worth saying out loud: the button simply stops promising anything.
    return null;
  }
}

/** Out of the pool, and the stream with it. Safe to call when not in it. */
export function leavePool() {
  const opening = stream;
  stream = null;
  ticket = null;
  seat = 0;
  rival = '';

  if (opening) {
    try {
      opening.abort();
    } catch {
      // Already gone, which is what we were after.
    }
  }
}

/**
 * Waits in the pool for a rival.
 *
 * Resolves with `{ ok: true, seat, rival }` the moment somebody turns up — and the stream
 * stays open afterwards, because that is now the match and `onShot` is how it is played.
 * Resolves with `{ ok: false, why }` if nobody does, if there is no server, or if the
 * player gives up first; `why` is 'timeout' in every case the player cares about, so the
 * caller can say "there was nobody" without inventing a reason.
 */
export function findRival({ name = '', onWaiting = () => {}, onShot = () => {}, onGone = () => {} } = {}) {
  leavePool();

  const opening = new AbortController();
  stream = opening;

  return new Promise(resolve => {
    let settled = false;
    let timer = null;

    const settle = (result, keepStream = false) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      if (!keepStream) leavePool();
      resolve(result);
    };

    timer = setTimeout(() => settle({ ok: false, why: 'timeout' }), POOL_WAIT_MS);

    const url = `${POOL_URL}?name=${encodeURIComponent(name)}`;

    (async () => {
      try {
        const res = await fetch(url, { signal: opening.signal, headers: { accept: 'text/event-stream' } });
        // El tipo, y no solo el código: un proxy contestando en nombre de un servidor que no
        // está devuelve un 200 con HTML, y leerlo entero sin encontrar un solo suceso acababa
        // en un "no hay nadie" mudo que mandaba a la CPU sin decir por qué.
        const type = res.headers.get('content-type') || '';
        if (!res.ok || !res.body || !type.includes('text/event-stream')) {
          settle({ ok: false, why: 'server' });
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Frames are separated by a blank line, which is where the server puts them.
          let cut;
          while ((cut = buffer.indexOf('\n\n')) >= 0) {
            const frame = framed(buffer.slice(0, cut));
            buffer = buffer.slice(cut + 2);
            if (!frame) continue;

            if (frame.event === 'hello') {
              ticket = frame.data.id;
            } else if (frame.event === 'waiting') {
              deliver(onWaiting, frame.data);
            } else if (frame.event === 'matched') {
              seat = Number(frame.data.seat) ? 1 : 0;
              rival = String(frame.data.rival || '');
              // The stream is kept: from here it is the match, not the queue.
              settle({ ok: true, seat, rival }, true);
            } else if (frame.event === 'shot') {
              deliver(onShot, String(frame.data.code || ''));
            } else if (frame.event === 'gone') {
              // A departure is told to the one who stayed, and it can happen long after
              // the match began — so this is a callback rather than an answer.
              deliver(onGone, frame.data);
              settle({ ok: false, why: 'gone' });
            }
          }
        }

        settle({ ok: false, why: 'closed' });
      } catch (err) {
        settle({ ok: false, why: err && err.name === 'AbortError' ? 'cancel' : 'server' });
      }
    })();
  });
}

/**
 * Hands one shot over. Fire and forget: a shot that cannot be delivered is a match that
 * is about to be told its rival has gone, and that is the thing worth saying, not this.
 */
export async function sendShot(code) {
  const id = room ? room.id : ticket;
  const url = room ? ROOM_SHOT_URL : POOL_SHOT_URL;
  if (!id || !code) return { ok: false };

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id, code }),
    });
    return { ok: res.ok };
  } catch {
    return { ok: false };
  }
}

// --- rooms ------------------------------------------------------------------

/** Which seat we hold in the room, 0 or 1. Player one's settings are the match's. */
export function roomSeat() {
  return room ? room.seat : 0;
}

/** The code of the room we are in, or '' when we are not in one. */
export function roomCode() {
  return room ? room.code : '';
}

/** Whether this match travels down a room rather than down a pool or a pocket. */
export function inRoom() {
  return !!room;
}

/**
 * Joins a room, making it if nobody has yet.
 *
 * The code is the room, so this is the same call for the player who invited and the player
 * who was invited: one of them arrives first and makes it, and the other takes the seat that
 * is left. `token` is the way back into the seat you had — the server hands one out the first
 * time and takes it back as proof — so a reload is a return rather than a second player.
 *
 * Resolves as soon as there is a seat: this is not a matchmaking wait, and there is no
 * timeout, because the person the code was sent to may take an hour. It resolves `ok: false`
 * when there is no server, which is the caller's cue to fall back to carrying the code by
 * hand.
 */
export function openRoom({
  code = '',
  name = '',
  token = '',
  seat = -1,
  onPeer = () => {},
  onShot = () => {},
  onGone = () => {},
  onClosed = () => {},
} = {}) {
  leavePool();
  leaveRoom();

  const opening = new AbortController();
  const mine = { code, id: null, seat: 0, token, abort: opening };
  room = mine;

  const url = `${ROOM_URL}?code=${encodeURIComponent(code)}&name=${encodeURIComponent(name)}`
    + `&token=${encodeURIComponent(token)}&seat=${seat}`;

  return new Promise(resolve => {
    let settled = false;

    const settle = result => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    /** Out of the room, whoever's fault it was. */
    const lost = why => {
      if (room === mine) room = null;
      return { ok: false, why };
    };

    (async () => {
      let res;
      try {
        res = await fetch(url, { signal: opening.signal, headers: { accept: 'text/event-stream' } });
      } catch (err) {
        settle(lost(err && err.name === 'AbortError' ? 'cancel' : 'server'));
        return;
      }

      if (!res.ok || !res.body) {
        settle(lost('server'));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      /** Whether there was ever a room to lose: a stream that dies before `hello` is a refusal. */
      let joined = false;

      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          let cut;
          while ((cut = buffer.indexOf('\n\n')) >= 0) {
            const frame = framed(buffer.slice(0, cut));
            buffer = buffer.slice(cut + 2);
            if (!frame) continue;

            if (frame.event === 'hello') {
              mine.id = frame.data.id;
              mine.seat = Number(frame.data.seat) ? 1 : 0;
              mine.token = String(frame.data.token || token);
              joined = true;
              // The stream stays: from here it is the match, not the doorway.
              settle({ ok: true, seat: mine.seat, token: mine.token, other: frame.data.other || null });
            } else if (frame.event === 'peer') {
              deliver(onPeer, frame.data);
            } else if (frame.event === 'shot') {
              deliver(onShot, String(frame.data.code || ''));
            } else if (frame.event === 'gone') {
              // A departure is told to the one who stayed, and the room goes on without them.
              deliver(onGone, frame.data);
            }
          }
        }

        if (room === mine) room = null;
        settle({ ok: false, why: 'closed' });
        if (joined) onClosed({ why: 'closed' });
      } catch (err) {
        const why = err && err.name === 'AbortError' ? 'cancel' : 'server';
        const was = room === mine;
        settle(lost(why));
        if (was && joined && why !== 'cancel') onClosed({ why });
      }
    })();
  });
}

/** Out of the room, and the stream with it. Safe to call when not in one. */
export function leaveRoom() {
  const mine = room;
  room = null;

  if (mine && mine.abort) {
    try {
      mine.abort.abort();
    } catch {
      // Already gone, which is what we were after.
    }
  }
}